import React from 'react';
import { Link } from "react-router-dom";
import { VaultLayout, VAULT_SUPPORT_EMAIL } from "../components/VaultLayout";

export const VaultSupport = ({ theme, onToggleTheme }) => {
  return (
    <VaultLayout
      title="Support"
      meta="Vault for iPhone"
      theme={theme}
      onToggleTheme={onToggleTheme}
    >
      <div className="vault-summary">
        <p>
          Need help with Vault? Email{' '}
          <a className="vault-link" href={`mailto:${VAULT_SUPPORT_EMAIL}?subject=Vault%20support`}>{VAULT_SUPPORT_EMAIL}</a>{' '}
          and include your iPhone model and iOS version.
        </p>
        <p>
          <strong>Never send your master PIN or any of your passwords.</strong> We will not ask for
          them, and we cannot see what is in your vault.
        </p>
      </div>

      <h2>Common questions</h2>

      <div className="vault-faq-item">
        <h3>I forgot my master PIN</h3>
        <ol>
          <li>On the lock screen, tap <strong>Forgot PIN?</strong></li>
          <li>Enter the reset email you added when you set up the app.</li>
          <li>Choose a new 6-digit PIN and enter it again to confirm.</li>
        </ol>
        <p>Your saved items are kept. No email is sent; the address is only checked on your iPhone.</p>
      </div>

      <div className="vault-faq-item">
        <h3>I forgot both my PIN and my reset email</h3>
        <p>
          The vault cannot be opened without one of them, and nobody, including us, can recover it
          for you. To start again, delete the app and install it again. This permanently erases the
          items in the vault.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>Face ID is not unlocking the app</h3>
        <ul>
          <li>Check that Face ID is set up on your iPhone.</li>
          <li>In iPhone Settings, find Vault and make sure Face ID is allowed.</li>
          <li>In Vault, open Settings and make sure <strong>Unlock with Face ID</strong> is on.</li>
        </ul>
        <p>Your master PIN always works, whether or not Face ID is available.</p>
      </div>

      <div className="vault-faq-item">
        <h3>Does Vault sync or back up my passwords?</h3>
        <p>
          No. Your vault exists only on the iPhone where you created it. If you delete the app,
          erase the phone or lose it, the vault is gone. Keep a separate record of anything you
          cannot afford to lose.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>Can I move my vault to a new iPhone?</h3>
        <p>
          Not at the moment. The vault is tied to the device it was created on, so on a new iPhone
          you set up the app again and re-enter your items.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>What happens to something I copy?</h3>
        <p>
          It goes onto the iPhone clipboard so you can paste it. With <strong>Clear Clipboard After
          30s</strong> turned on in Settings, Vault clears the clipboard about 30 seconds later while
          the app is still running.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>When does the vault lock itself?</h3>
        <p>
          When you leave the app. In Settings, <strong>Auto-Lock</strong> lets you choose whether
          that happens immediately, after 1 minute or after 5 minutes. You can also tap <strong>Lock
          Vault Now</strong> at any time.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>What does the &ldquo;Weak&rdquo; label mean?</h3>
        <p>
          It marks a login or Wi-Fi password that is short or uses few kinds of characters. Consider
          replacing it; the generator on the New Item screen can create a strong one.
        </p>
      </div>

      <div className="vault-faq-item">
        <h3>How do I delete my data?</h3>
        <p>
          To remove one item, open it and tap <strong>Delete Item</strong>. To remove everything,
          delete the app from your iPhone.
        </p>
      </div>

      <h2>Privacy</h2>
      <p>
        Vault does not collect any information. The details are in the{' '}
        <Link className="vault-link" to="/vault/privacy">Privacy Policy</Link>.
      </p>
    </VaultLayout>
  )
}
